/* RESPONSIBLE TEAM: team-channels */
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type Transition from '@ember/routing/-private/transition';
import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import ENV from 'embercom/config/environment';
import type EmailController from 'embercom/controllers/apps/app/settings/channels/email';
import { get } from 'embercom/lib/ajax';
import type DomainService from 'embercom/services/domain-service';
import type IntlService from 'embercom/services/intl';
import { hash } from 'rsvp';

const CAN_MANAGE_MESSAGES_SETTINGS = 'can_manage_messages_settings';

export default class EmailRoute extends Route {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare router: RouterService;
  @service declare permissionsService: $TSFixMe;
  @service declare domainService: DomainService;

  get titleToken() {
    return this.intl.t(`new-settings.submenu.channels.email`);
  }

  get app() {
    return this.appService.app;
  }

  async model() {
    let canAccessProactiveSettings = await this.permissionsService.currentAdminCan(
      CAN_MANAGE_MESSAGES_SETTINGS,
    );
    let canAccessWorkspaceSettings = await this.permissionsService.currentAdminCan(
      'can_access_workspace_settings',
    );
    let canAccessProductSettings = await this.permissionsService.currentAdminCan(
      'can_access_product_settings',
    );
    return hash({
      ...(canAccessProactiveSettings && { spambotContact: this.store.findAll('spambot-contact') }),
      ...(canAccessProductSettings && {
        conversationHistorySettings: this.store.findRecord(
          'email-conversation-history-settings',
          this.app.id,
        ),
      }),
      ...(canAccessProductSettings && {
        convertInboundEmailsToTicketsSettings: this.store.findRecord(
          'email-convert-inbound-emails-to-tickets-settings',
          this.app.id,
        ),
      }),
      hasLiveConvertToTicketWorkflows: get(
        '/ember/operator_workflows/has_live_convert_to_ticket_workflows',
        {
          app_id: this.app.id,
        },
      ),
      ...(canAccessProductSettings && {
        stripInboundEmailLinksSettings: this.store.findRecord(
          'strip-inbound-email-links-settings',
          this.app.id,
        ),
      }),
      ...(canAccessProductSettings && {
        emailPoweredBySettings: this.store.findRecord('email-powered-by-settings', this.app.id),
      }),
      ...(canAccessProductSettings && {
        operatorName: this.store.findRecord('operator-identity', 1),
      }),
      ...(canAccessProductSettings && {
        spamSettings: this.store.findRecord('email-spam-settings', this.app.id),
      }),
      ...(canAccessProductSettings && {
        continuedConversationsSettings: this.store.findRecord(
          'email-continued-conversations-settings',
          this.app.id,
        ),
      }),
      ...(canAccessProductSettings && {
        emailAutoReplySettings: this.store.findRecord('email-auto-reply-settings', this.app.id),
      }),
      ...(canAccessProactiveSettings && {
        messagingSettings: this.store.findRecord('messaging-settings', this.app.id),
      }),
      ...(canAccessWorkspaceSettings && {
        attachmentSettings: this.store.findRecord('attachment-settings', this.app.id),
      }),
      emailDomains: this.app.loadEmailDomains(),
      ...(canAccessProductSettings && {
        detectForwardedSettings: this.store.findRecord(
          'email-detect-forwarded-settings',
          this.app.id,
        ),
      }),
      ...(canAccessProductSettings &&
        this.app.canUseFeature('team-email-show-subject-split-setting') && {
          splitSubjectSettings: this.store.findRecord('email-split-subject-settings', this.app.id),
        }),
      ...(canAccessProactiveSettings && {
        emailTemplatesSettings: this.store.findRecord('email-templates-settings', this.app.id),
      }),
      emailTemplateTitles: this.store.findAll('email-template-titles'),
      linkBrandingSettings: this.store.query('settings/custom-email-assets-domain', {}),
      ...(canAccessProactiveSettings && {
        dedicatedIp: this.store.query('settings/dedicated-ip', {}),
      }),
      canAccessProactiveSettings,
      canAccessWorkspaceSettings,
      canAccessProductSettings,
    });
  }

  resetController(controller: EmailController, isExiting: any) {
    if (isExiting) {
      controller.set('highlight', null);
      controller.set('step', null);
      controller.set('domainName', undefined);
      controller.set('email', null);
    }
  }

  async beforeModel(transition: Transition) {
    super.beforeModel(transition);
    let permission = 'can_access_product_settings';
    this.permissionsService.ensurePermissionWhenTransitioning(permission, transition);
    this._notifyConfirmationForQueryParams(transition.to.queryParams);
    await this.domainService.load();
  }

  _notifyConfirmationForQueryParams(queryParams: $TSFixMe) {
    if (isPresent(queryParams) && isPresent(queryParams['verifiedCustomEmail'])) {
      let email = `${queryParams['verifiedCustomEmail']}`;
      let confirmationText = this.intl.t('settings.senders.outbound-sender-settings.verified', {
        email,
      });
      this.notificationsService.notifyConfirmation(confirmationText, ENV.APP._15000MS);
      delete queryParams['verifiedCustomEmail'];
      this.router.replaceWith('apps.app.settings.channels.email', this.appService.app, {
        queryParams,
      });
    }
  }
}
